import { useCallback, useRef } from 'react';
import { useGetStartedTour } from './GetStartedTour.context';
import type { GetStartedTaskId, GetStartedTargetRef, GetStartedTargetRegistrationOptions } from './GetStartedOnboarding.types';

/**
 * Registers the DOM element a tour step points at. Returns a callback ref to spread onto it.
 */
export const useGetStartedTarget = (taskId: GetStartedTaskId, options?: GetStartedTargetRegistrationOptions): GetStartedTargetRef => {
  const { registerTarget } = useGetStartedTour();

  // Latest-value cache for the options, so an inline `resolveAnchor` doesn't churn the callback ref
  // (a new callback ref identity makes React detach and reattach the node every render).
  //
  // Written during render rather than in an effect. React commits bottom-up, so the ref on a host
  // element inside this component is attached BEFORE this component's own layout effect runs — a
  // layout-effect write would therefore land after the registration it exists to feed.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Registration hands the options to the provider BY VALUE, so passing `optionsRef.current`
  // directly would freeze whichever `resolveAnchor` happened to be current at registration, and a
  // caller whose resolver closes over state would resolve against the mount-time closure forever.
  // This wrapper reads through the ref instead.
  //
  // It lives in a ref, built ONCE, because the provider dedupes a re-registration on
  // `resolveAnchor` identity (GetStartedTourProvider). A wrapper rebuilt per call never matched, so
  // every invocation wrote a new target Map — and `useCombinedRefs` re-invokes forwarded refs on
  // EVERY render, which turned that into register -> setTargets -> context change -> re-render ->
  // register. See PRNT-5148.
  const liveOptionsRef = useRef<GetStartedTargetRegistrationOptions>({
    resolveAnchor: anchorCandidate => {
      const resolve = optionsRef.current?.resolveAnchor;
      // A resolver returning null means "the descendant isn't mounted yet" and must survive as
      // null — `useTargetRect` retries on it. Only the ABSENCE of a resolver falls back to the
      // element, matching the provider's default.
      return resolve ? resolve(anchorCandidate) : anchorCandidate;
    },
  });

  return useCallback<GetStartedTargetRef>(
    element => {
      registerTarget(taskId, element, liveOptionsRef.current);
    },
    [registerTarget, taskId],
  );
};
